import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent
} from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { PoNotificationService } from '@po-ui/ng-components';

import { LoginService } from '../services/login.service';
import { ConfigService } from '../services/config.service';
import { ProtheusAuthService } from '../services/protheus-auth.service';
import { FilialService } from '../services/filial.service';

// Paths que NAO devem receber o header x-app-token (login/reset publicos do portal)
const PUBLIC_APP_PATHS = ['portalnf/auth/v1', 'portalnf/auth/sendreset', 'portalnf/auth/reset'];

// Requests internas do OAuth2 do Protheus nao sao interceptadas (chamadas via HttpBackend)
let isRefreshingApp = false;
const appRefreshSubject = new BehaviorSubject<string | null>(null);

export const basicAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const loginService = inject(LoginService);
  const poNotification = inject(PoNotificationService);
  const configService = inject(ConfigService);
  const protheusAuth = inject(ProtheusAuthService);
  const filialService = inject(FilialService);

  return protheusAuth.getToken().pipe(
    switchMap(protheusToken => {
      const authReq = addAuth(req, configService.getHostRest(), protheusToken, loginService.getToken(), filialService.currentCnpj());

      return next(authReq).pipe(
        catchError((err: HttpErrorResponse) => {
          const path = req.url;
          const isPublicApp = PUBLIC_APP_PATHS.some(p => path.includes(p));

          // 401 do Protheus (token de integracao expirou) — renova e tenta de novo uma vez
          if (err.status === 401 && isProtheusAuthError(err)) {
            protheusAuth.invalidate();
            return protheusAuth.getToken().pipe(
              switchMap(newToken =>
                next(addAuth(stripBase(req, configService.getHostRest()), configService.getHostRest(), newToken, loginService.getToken(), filialService.currentCnpj()))
              )
            );
          }

          // 401 do app (JWT do portal expirou) — tenta refresh do app JWT
          if (err.status === 401 && !isPublicApp) {
            return handleAppUnauthorized(authReq, next, configService, loginService, protheusAuth, router, poNotification, filialService);
          }

          // Endpoints publicos (login/reset) tratam o erro no proprio componente,
          // para exibir mensagem amigavel ("E-mail ou senha invalidos") ao inves de "401 - ..."
          if (isPublicApp) {
            return throwError(() => err);
          }

          const msg = formatError(err);
          if (err.status === 0) {
            poNotification.error({ message: 'Ops! Nosso servidor nao esta respondendo' });
          } else {
            poNotification.error({ message: msg });
          }
          return throwError(() => msg);
        })
      );
    })
  );
};

function handleAppUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  config: ConfigService,
  login: LoginService,
  protheus: ProtheusAuthService,
  router: Router,
  notif: PoNotificationService,
  filial: FilialService
): Observable<HttpEvent<unknown>> {

  if (!sessionStorage.getItem('refresh_token')) {
    login.logout();
    return throwError(() => 'Sessao expirada');
  }

  if (isRefreshingApp) {
    return appRefreshSubject.pipe(
      filter(t => t !== null),
      take(1),
      switchMap(() =>
        protheus.getToken().pipe(
          switchMap(protheusToken =>
            next(addAuth(stripBase(req, config.getHostRest()), config.getHostRest(), protheusToken, login.getToken(), filial.currentCnpj()))
          )
        )
      )
    );
  }

  isRefreshingApp = true;
  appRefreshSubject.next(null);

  return login.refreshToken().pipe(
    switchMap(res => {
      isRefreshingApp = false;
      appRefreshSubject.next(res.access_token!);
      return protheus.getToken().pipe(
        switchMap(protheusToken =>
          next(addAuth(stripBase(req, config.getHostRest()), config.getHostRest(), protheusToken, res.access_token!, filial.currentCnpj()))
        )
      );
    }),
    catchError(err => {
      isRefreshingApp = false;
      notif.error({ message: 'Sessao expirada. Faca login novamente.' });
      login.logout();
      router.navigate(['/login']);
      return throwError(() => err);
    })
  );
}

function stripBase(req: HttpRequest<unknown>, baseUrl: string): HttpRequest<unknown> {
  const prefix = `${baseUrl}/`;
  if (req.url.startsWith(prefix)) {
    return req.clone({ url: req.url.substring(prefix.length) });
  }
  return req;
}

function addAuth(
  req: HttpRequest<unknown>,
  baseUrl: string,
  protheusToken: string,
  appToken: string | null,
  filialCnpj: string | null
): HttpRequest<unknown> {
  // Consultas de apoio dos combos NAO entram na contagem de requisicoes
  // pendentes do PO-UI. Contabilizadas, elas disparam o overlay global
  // "Carregando ...", que rouba o foco do po-combo; ao perder o foco o
  // componente roda verifyValidOption(), limpa o valor e fecha a lista aberta.
  // O sintoma era a lista aparecer e sumir, sem conseguir selecionar.
  const ehLookup = req.url.includes('portalnf/regras/lookup');

  const headers: Record<string, string> = {
    'Accept': 'application/json; charset=UTF-8',
    'Content-Type': 'application/json',
    'X-PO-No-Count-Pending-Requests': ehLookup ? 'true' : 'false'
  };
  // Authorization (Bearer de integracao) so quando houver token. No modelo
  // BFF o front nao minta Bearer (protheusToken vazio) - quem injeta e o BFF.
  if (protheusToken) {
    headers['Authorization'] = `Bearer ${protheusToken}`;
  }
  if (appToken) {
    headers['x-app-token'] = appToken;
  }
  if (filialCnpj && req.url.includes('portalnf/')) {
    headers['CNPJ'] = filialCnpj;
  }
  // URL: aceita absoluta (http) ou ja prefixada com baseUrl ("/rest/...").
  // Senao prefixa (fluxo pos-stripBase, req.url = "portalnf/...").
  const url = (req.url.startsWith('http') || req.url.startsWith(`${baseUrl}/`))
    ? req.url
    : `${baseUrl}/${req.url}`;
  return req.clone({ setHeaders: headers, url });
}

function isProtheusAuthError(err: HttpErrorResponse): boolean {
  // Heuristica: o Protheus responde com "invalid_token"/"expired" no corpo quando o Bearer falha
  const body = err.error;
  const msg = (typeof body === 'string' ? body : body?.message || body?.error || '').toString().toLowerCase();
  return msg.includes('invalid_token') || msg.includes('expired') || msg.includes('unauthorized');
}

function formatError(err: HttpErrorResponse): string {
  const detail = err.error?.message || err.error?.errorMessage || err.message;
  return `${err.status} - ${detail}`;
}
